import { useState, useEffect } from 'react';
import { Input, Button } from '@/components/ui';
import { PROJECT_STATUSES, type ProjectWithCustomer, type ProjectStatus } from '../types';

interface ProjectEditFormProps {
  project: ProjectWithCustomer;
  onSave: (data: {
    project_name: string;
    status: ProjectStatus;
    start_date: string | null;
    end_date: string | null;
  }) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function ProjectEditForm({
  project,
  onSave,
  onCancel,
  isSubmitting,
  error,
}: ProjectEditFormProps) {
  const [formData, setFormData] = useState({
    name: project.project_name,
    status: project.status,
    start_date: project.start_date || '',
    end_date: project.end_date || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when project changes
  useEffect(() => {
    setFormData({
      name: project.project_name,
      status: project.status,
      start_date: project.start_date || '',
      end_date: project.end_date || '',
    });
    setErrors({});
  }, [project]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    await onSave({
      project_name: formData.name.trim(),
      status: formData.status as ProjectStatus,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    });
  };

  // Check for date warning
  const hasDateWarning =
    formData.start_date &&
    formData.end_date &&
    formData.end_date < formData.start_date;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-500 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Project Name */}
      <Input
        label="Project Name"
        name="name"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        placeholder="Kitchen Renovation"
        error={errors.name}
        disabled={isSubmitting}
        required
      />

      {/* Status */}
      <div className="w-full">
        <label htmlFor="status" className="input-label">
          Status
        </label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={(e) => handleChange('status', e.target.value)}
          className="input-field"
          disabled={isSubmitting}
        >
          {PROJECT_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Start Date (Optional)"
          name="start_date"
          type="date"
          value={formData.start_date}
          onChange={(e) => handleChange('start_date', e.target.value)}
          disabled={isSubmitting}
        />
        <Input
          label="End Date (Optional)"
          name="end_date"
          type="date"
          value={formData.end_date}
          onChange={(e) => handleChange('end_date', e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      {/* Date Warning */}
      {hasDateWarning && (
        <p className="text-sm text-amber-600 flex items-center gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          End date is before start date
        </p>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-secondary-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 sm:flex-none"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          className="flex-1 sm:flex-none"
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
}